import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import type {
  InitializeTrainerSessionRequestAnswerMethod,
  InitializeTrainerSessionRequestDirection,
  SaveTrainerResponseRequest,
  TrainerSessionResponseCue,
  TrainerSessionResponseSessionProgress,
  TrainerSessionStatisticsResponse,
} from '@thiememeulenhoff/thor-client';
import { TrainerService as ThorTrainerService } from '@thiememeulenhoff/thor-client';
import { firstValueFrom } from 'rxjs';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';

type TrainerState = {
  sessionId: string | null;
  targetLocale: string | null;
  cue: TrainerSessionResponseCue | null;
  sessionProgress: TrainerSessionResponseSessionProgress | null;
  statistics: TrainerSessionStatisticsResponse | null;
};

const initialState: TrainerState = {
  sessionId: null,
  targetLocale: null,
  cue: null,
  sessionProgress: null,
  statistics: null,
};

export const TrainerStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withMethods((store, trainerService = inject(ThorTrainerService)) => ({
    async startSession(
      arrangementCode: string,
      wordListId: string,
      timezone: string,
      direction: InitializeTrainerSessionRequestDirection,
      answerMethod: InitializeTrainerSessionRequestAnswerMethod,
    ): Promise<void> {
      patchState(store, {
        requestStatus: 'loading',
        sessionId: null,
        targetLocale: null,
        cue: null,
        sessionProgress: null,
        statistics: null,
      });
      try {
        const session = await firstValueFrom(
          trainerService.initializeTrainerSession({
            arrangementCode,
            exerciseType: 'wordlist',
            exerciseId: wordListId,
            answerMethod,
            randomOrder: true,
            timezone,
            direction,
          }),
        );

        patchState(store, {
          requestStatus: 'success',
          sessionId: session.sessionId,
          targetLocale: session.targetLocale,
          cue: session.cue,
          sessionProgress: session.sessionProgress,
          statistics: null,
        });
      } catch (error) {
        patchState(store, {
          requestStatus: toRequestError(error),
        });
        throw error;
      }
    },
    async submitAnswer(payload: SaveTrainerResponseRequest): Promise<void> {
      patchState(store, { requestStatus: 'pending' });

      try {
        const progress = await firstValueFrom(
          trainerService.saveTrainerResponse(payload),
        );
        patchState(store, {
          requestStatus: 'success',
          sessionId: progress.sessionId,
          cue: progress.cue,
          sessionProgress: progress.sessionProgress,
        });
      } catch (error) {
        patchState(store, { requestStatus: toRequestError(error) });
        throw error;
      }
    },
    async finishSession(): Promise<void> {
      const sessionId = store.sessionId();

      if (sessionId === null) {
        throw new Error('Trainer session is not initialized');
      }

      patchState(store, { requestStatus: 'pending' });

      try {
        const statistics = await firstValueFrom(
          trainerService.finishTrainerSession(sessionId),
        );

        patchState(store, {
          requestStatus: 'success',
          statistics,
        });
      } catch (error) {
        patchState(store, { requestStatus: toRequestError(error) });
        throw error;
      }
    },
  })),
);
